import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Truck } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Motion";
import { CORAL_TAXONOMY, FISH_TYPES, EQUIPMENT_TYPES, EQUIPMENT_CONDITIONS, subtypeLabel, subtypesFor } from "@/lib/corals";
import { toast } from "sonner";

const FILTERS = [
  { k: "all", label: "Everything" },
  { k: "coral", label: "Corals" },
  { k: "fish", label: "Fish" },
  { k: "equipment", label: "Tanks & Equipment" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [coralType, setCoralType] = useState("all");
  const [coralSub, setCoralSub] = useState("all");
  const [fishType, setFishType] = useState("all");
  const [condition, setCondition] = useState("all");
  const [equipType, setEquipType] = useState("all");
  const [loading, setLoading] = useState(true);
  const { add } = useCart();

  useEffect(() => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const pickFilter = (k) => {
    setFilter(k);
    setCoralType("all");
    setCoralSub("all");
    setFishType("all");
    setCondition("all");
    setEquipType("all");
  };
  const pickCoralType = (t) => {
    setCoralType(t);
    setCoralSub("all");
  };

  const shown = useMemo(() => {
    let list = filter === "all" ? products : products.filter((p) => p.category === filter);
    if (filter === "coral" && coralType !== "all") {
      list = list.filter((p) => p.coral_type === coralType);
      if (coralSub !== "all") list = list.filter((p) => p.coral_subtype === coralSub);
    }
    if (filter === "fish" && fishType !== "all") {
      list = list.filter((p) => p.fish_type === fishType);
    }
    if (filter === "equipment") {
      if (condition !== "all") list = list.filter((p) => p.condition === condition);
      if (equipType !== "all") list = list.filter((p) => p.equipment_type === equipType);
    }
    return list;
  }, [products, filter, coralType, coralSub, fishType, condition, equipType]);

  const subOptions = coralType !== "all" ? subtypesFor(coralType) : [];
  const onAdd = (p) => { add(p); toast.success(`${p.name} added to cart`); };

  // total units in stock matching a predicate
  const stock = (fn) => products.reduce((n, p) => (fn(p) && p.stock > 0 ? n + p.stock : n), 0);
  const coralAll = stock((p) => p.category === "coral");
  const fishAll = stock((p) => p.category === "fish");
  const equipAll = stock((p) => p.category === "equipment");
  const condMatch = (p) => condition === "all" || p.condition === condition;
  const Badge = ({ n, active }) => (
    <span data-testid="stock-count" className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-mono ${active ? "bg-void/20 text-void" : "bg-white/10 text-white/60"}`}>{n}</span>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-36 pb-28 min-h-screen">
      <Reveal>
        <span className="mono-label">The Collection</span>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-4 mb-3">Exotic Corals & Reef Fish for Sale</h1>
        <p className="text-white/50 max-w-2xl">
          Shop <b className="text-white/70">aquacultured exotic corals</b> — SPS (Acroporas, Montiporas), LPS
          (Chalices, Euphyllias, Acans), Anemones and soft corals (Zoas, Mushrooms, Leathers) — plus hand-selected
          reef fish, <b className="text-white/70">imported directly from Australia & Indonesia</b>. Every coral is
          tank-grown, healthy and photographed WYSIWYG. Add to cart and check out securely — livestock ships when
          conditions are safe.
        </p>
      </Reveal>

      <div className="mt-8 glass rounded-2xl border border-cyan/20 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6" data-testid="shipping-banner">
        <span className="mono-label text-cyan flex items-center gap-2"><Truck size={16} /> Shipping</span>
        {filter === "equipment" ? (
          <p className="text-white/70 text-sm">
            Tanks & gear ship year-round. <b className="text-white">Some large or used items are local pickup only</b> (Miami / Hialeah) — look for the <span className="text-toxic">Pickup only</span> tag.
            <Link to="/policy" className="text-cyan hover:underline ml-1">Read policy →</Link>
          </p>
        ) : (
          <p className="text-white/70 text-sm">
            <b className="text-white">$50 flat</b> livestock shipping · <b className="text-cyan">FREE over $450</b> ·
            <b className="text-white"> Ships Tue & Wed only</b> ·
            <span className="text-toxic"> 3-hour DOA claim window</span> on delivery.
            <Link to="/policy" className="text-cyan hover:underline ml-1">Read policy →</Link>
          </p>
        )}
      </div>

      {/* Primary filters */}
      <div className="flex gap-3 mt-10 mb-6 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => pickFilter(f.k)}
            className={`mono-label px-5 py-2.5 rounded-full border transition-all ${filter === f.k ? "bg-cyan text-void border-cyan" : "border-white/15 text-white/60 hover:border-white/40"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Coral category filters */}
      {filter === "coral" && (
        <div className="flex gap-2.5 mb-6 flex-wrap" data-testid="coral-type-filters">
          <button data-testid="coral-type-all" onClick={() => pickCoralType("all")}
            className={`text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-all inline-flex items-center ${coralType === "all" ? "bg-white text-void border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}>
            All Corals <Badge n={coralAll} active={coralType === "all"} />
          </button>
          {CORAL_TAXONOMY.map((c) => (
            <button key={c.type} data-testid={`coral-type-${c.type}`} onClick={() => pickCoralType(c.type)}
              className={`text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-all inline-flex items-center ${coralType === c.type ? "bg-white text-void border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}>
              {c.label} <Badge n={stock((p) => p.category === "coral" && p.coral_type === c.type)} active={coralType === c.type} />
            </button>
          ))}
        </div>
      )}

      {/* Coral subtype filters */}
      {filter === "coral" && subOptions.length > 0 && (
        <div className="flex gap-2 mb-10 flex-wrap pl-1" data-testid="coral-subtype-filters">
          <button data-testid="coral-subtype-all" onClick={() => setCoralSub("all")}
            className={`text-[11px] font-mono px-3 py-1.5 rounded-full border transition-all inline-flex items-center ${coralSub === "all" ? "bg-cyan/20 text-cyan border-cyan/50" : "border-white/10 text-white/45 hover:border-cyan/40"}`}>
            All {coralType} <Badge n={stock((p) => p.category === "coral" && p.coral_type === coralType)} active={false} />
          </button>
          {subOptions.map((s) => (
            <button key={s} data-testid={`coral-subtype-${s}`} onClick={() => setCoralSub(s)}
              className={`text-[11px] font-mono px-3 py-1.5 rounded-full border transition-all inline-flex items-center ${coralSub === s ? "bg-cyan/20 text-cyan border-cyan/50" : "border-white/10 text-white/45 hover:border-cyan/40"}`}>
              {subtypeLabel(s)} <Badge n={stock((p) => p.category === "coral" && p.coral_type === coralType && p.coral_subtype === s)} active={false} />
            </button>
          ))}
        </div>
      )}

      {/* Fish category filters */}
      {filter === "fish" && (
        <div className="flex gap-2.5 mb-12 flex-wrap" data-testid="fish-type-filters">
          <button data-testid="fish-type-all" onClick={() => setFishType("all")}
            className={`text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-all inline-flex items-center ${fishType === "all" ? "bg-white text-void border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}>
            All Fish <Badge n={fishAll} active={fishType === "all"} />
          </button>
          {FISH_TYPES.map((t) => (
            <button key={t} data-testid={`fish-type-${t}`} onClick={() => setFishType(t)}
              className={`text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-all inline-flex items-center ${fishType === t ? "bg-white text-void border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}>
              {t} <Badge n={stock((p) => p.category === "fish" && p.fish_type === t)} active={fishType === t} />
            </button>
          ))}
        </div>
      )}

      {/* Equipment condition (New/Used) + type filters */}
      {filter === "equipment" && (
        <>
          <div className="flex gap-2.5 mb-6 flex-wrap" data-testid="equipment-condition-filters">
            <button data-testid="equipment-condition-all" onClick={() => setCondition("all")}
              className={`text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-all inline-flex items-center ${condition === "all" ? "bg-white text-void border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}>
              All <Badge n={equipAll} active={condition === "all"} />
            </button>
            {EQUIPMENT_CONDITIONS.map((c) => (
              <button key={c.value} data-testid={`equipment-condition-${c.value}`} onClick={() => setCondition(c.value)}
                className={`text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-all inline-flex items-center ${condition === c.value ? "bg-white text-void border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}>
                {c.label} <Badge n={stock((p) => p.category === "equipment" && p.condition === c.value)} active={condition === c.value} />
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-12 flex-wrap pl-1" data-testid="equipment-type-filters">
            <button data-testid="equipment-type-all" onClick={() => setEquipType("all")}
              className={`text-[11px] font-mono px-3 py-1.5 rounded-full border transition-all inline-flex items-center ${equipType === "all" ? "bg-cyan/20 text-cyan border-cyan/50" : "border-white/10 text-white/45 hover:border-cyan/40"}`}>
              All types <Badge n={stock((p) => p.category === "equipment" && condMatch(p))} active={false} />
            </button>
            {EQUIPMENT_TYPES.map((t) => (
              <button key={t} data-testid={`equipment-type-${t}`} onClick={() => setEquipType(t)}
                className={`text-[11px] font-mono px-3 py-1.5 rounded-full border transition-all inline-flex items-center ${equipType === t ? "bg-cyan/20 text-cyan border-cyan/50" : "border-white/10 text-white/45 hover:border-cyan/40"}`}>
                {t} <Badge n={stock((p) => p.category === "equipment" && condMatch(p) && p.equipment_type === t)} active={false} />
              </button>
            ))}
          </div>
        </>
      )}

      {filter === "all" && <div className="mb-12" />}

      {loading ? (
        <div className="grid md:grid-cols-3 gap-6">
          {Array(6).fill(0).map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-white/5 animate-pulse" />)}
        </div>
      ) : shown.length === 0 ? (
        <p className="text-white/40 font-mono py-20 text-center" data-testid="shop-empty">No items in this category yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
          {shown.map((p, i) => <ProductCard key={p.id} product={p} onAdd={onAdd} index={i} />)}
        </div>
      )}
    </div>
  );
}
